"use client";

import { useTranslations } from "next-intl";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import useSWR from "swr";
import { AnimatePresence, motion } from "framer-motion";
import { BasketSummary, Edgeconfig, WhitelabelConfig } from "@types";
import { CheckoutDetails } from "@features/checkout/types";
import {
  useHydrated,
  useLatestCookieValue,
  useSupabaseSession,
} from "@utils/hooks";
import {
  authorizedFetcher,
  basketSummaryFetcher,
  fetcher,
  handle429s,
} from "@utils/fetchers";
import { joinClassNames } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import CartOpenStateWrapper from "../components/cartOpenStateWrapper";
import MobileMenu from "../components/mobileMenu";
import Cart from "../components/cart";
import { LocaleSwitcher } from "@features/localeSwitcher";
import { Link as StyledLink } from "@components/theming";
import GenreNavMenu from "../components/genreNavMenu";
import SearchResults from "@components/searchResults";
import Banner from "../components/banner";
import Logo from "@features/iTICKET-logo";
import AccountPopover from "../components/account";

interface Props {
  settings?: WhitelabelConfig["settings"];
  language?: WhitelabelConfig["website"]["language"];
  banner?: Edgeconfig["banner"];
  isDraftMode?: boolean;
}

const Navbar = ({ settings, language, banner, isDraftMode }: Props) => {
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const hydrated = useHydrated();
  const session = useSupabaseSession();
  const pathname = usePathname();
  const isShopsite = useIsShopsite();

  const t = useTranslations("Navbar");

  const { data: basket } = useSWR<BasketSummary>(
    basketKey ? `basket/${basketKey}/summary` : null,
    basketSummaryFetcher,
    {
      refreshInterval: 60 * 1000,
      refreshWhenHidden: true,
      onErrorRetry: handle429s,
    }
  );

  const { data: checkoutDetails } = useSWR<CheckoutDetails>(
    session &&
      session.access_token &&
      basketKey &&
      (pathname === "/cart/checkout/payment" ||
        pathname === "/cart" ||
        pathname.includes("status"))
      ? [`basket/${basketKey}/checkout`, session.access_token]
      : basketKey &&
        hydrated &&
        !session &&
        (pathname === "/cart/checkout/payment" ||
          pathname === "/cart" ||
          pathname.includes("status"))
      ? `basket/${basketKey}/checkout`
      : null,
    session && session.access_token ? authorizedFetcher : fetcher
  );

  return (
    <div
      className={joinClassNames(
        "z-50 w-full",
        isShopsite
          ? banner?.enabled
            ? "h-[138px]"
            : "h-[5.5rem]"
          : "sticky top-0"
      )}
      style={{
        backgroundColor: settings?.header.colour,
      }}
      id="navbar"
      data-testid="navbar"
    >
      <header
        className={isShopsite ? "fixed top-0 z-50 w-screen bg-black" : ""}
      >
        {hydrated && banner && isShopsite ? (
          <Banner isDraftMode={isDraftMode} banner={banner} />
        ) : (
          <></>
        )}
        <nav
          style={
            {
              backgroundColor: settings ? settings.header.colour : "#000000",
              "--navbar-height": settings
                ? `${settings.header.height}px`
                : undefined,
            } as React.CSSProperties
          }
          className={joinClassNames(
            "flex items-center justify-between",
            isShopsite ? "mx-auto max-w-7xl p-3 lg:px-8" : "w-full px-8",
            !isShopsite ? `h-[80px] sm:h-[--navbar-height]` : ""
          )}
          aria-label="Global"
        >
          <div
            className={joinClassNames(
              "flex items-center gap-8",
              isShopsite ? "lg:flex-1" : ""
            )}
          >
            <Link
              href={"/"}
              className={
                isShopsite ? "-m-1.5 p-1.5" : "relative w-36 sm:w-48 h-12"
              }
            >
              {settings?.header.logo ? (
                <Image
                  src={settings.header.logo}
                  fill
                  alt="logo"
                  sizes="width: 100%, height: 100%"
                  className="object-contain"
                  unoptimized
                />
              ) : (
                <>
                  <span className="sr-only">iTICKET</span>
                  <Logo size="20" />
                </>
              )}
            </Link>
            {isShopsite && (
              <div className="hidden lg:block w-full">
                <GenreNavMenu />
              </div>
            )}
          </div>
          <div
            className={joinClassNames(
              "flex items-center",
              isShopsite ? "" : "justify-between"
            )}
          >
            {isShopsite && (
              <div className="hidden lg:flex">
                <SearchResults />
              </div>
            )}
            <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:ml-4">
              {session ? (
                <AccountPopover />
              ) : (
                <StyledLink
                  href="/account/sign-in"
                  size="3"
                  weight="medium"
                  sharedClasses="text-white"
                  shopsiteClasses="text-sm font-semibold leading-6 text-white"
                >
                  {t("signin")}
                </StyledLink>
              )}
            </div>
            <AnimatePresence mode="wait">
              <div className="lg:hidden">
                {(!basket || basket.Quantity === 0) && (
                  <motion.div
                    key="mobile_searchbar"
                    initial="hidden"
                    animate="show"
                    exit="hidden"
                    variants={{
                      hidden: {
                        opacity: 0,
                        x: "1rem",
                        transition: { duration: 0.125 },
                      },
                      show: {
                        opacity: 1,
                        x: 0,
                      },
                    }}
                    transition={{ duration: 0.25 }}
                  >
                    <SearchResults />
                  </motion.div>
                )}
              </div>
              {basket && basket.Quantity > 0 && (
                <motion.div
                  key="cart_component"
                  data-testid={`cart`}
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={{
                    hidden: {
                      width: 0,
                      marginLeft: 0,
                    },
                    show: {
                      width: "auto",
                      marginLeft: "1rem",
                      transition: { delayChildren: 0.1 },
                    },
                  }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <motion.div
                    variants={{
                      hidden: {
                        opacity: 0,
                        x: "6rem",
                        transition: { x: { delay: 0.1 } },
                      },
                      show: {
                        opacity: 1,
                        x: 0,
                        transition: { opacity: { delay: 0.2 } },
                      },
                    }}
                    transition={{ duration: 0.25 }}
                  >
                    <CartOpenStateWrapper>
                      {(open, setOpen) => (
                        <Cart
                          key={basket.Ttl}
                          open={open}
                          setOpen={setOpen}
                          basket={basket}
                          isPended={checkoutDetails?.IsPended ?? false}
                        />
                      )}
                    </CartOpenStateWrapper>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            {language && language.supportedLanguages.length > 1 && (
              <div className="hidden md:block ml-4">
                <LocaleSwitcher
                  language={language}
                  disabled={checkoutDetails?.IsPended}
                />
              </div>
            )}
            <div className="flex ml-10 lg:ml-0 lg:hidden">
              <MobileMenu
                showSearch={!!(basket && basket.Quantity)}
                language={language}
                colour={settings?.header.colour}
                textColour={settings?.header.textColour}
              />
            </div>
          </div>
        </nav>
      </header>
    </div>
  );
};

export default Navbar;
